/* eslint-disable @typescript-eslint/no-explicit-any */
import { CopyOutlined } from "@ant-design/icons";
import { Button, Dropdown, Flex, Input, Modal, Select } from "antd";
import dayjs from "dayjs";
import { Link, useNavigate } from "react-router";
import {
  useGetPromotionsQuery,
  useUpdatePromotionMutation,
} from "../../../app/features/adminPromotions/adminPromotions.api";
import type { PromotionItem } from "../../../app/features/adminPromotions/adminPromotions.types";
import ActionIcon from "../../../assets/icons/ActionIcon";
import AddIcon from "../../../assets/icons/AddIcon";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import CustomTable from "../../../components/CustomTable";
import CustomTitle from "../../../components/CustomTitle";
import StatusTag from "../../../components/StatusTag";
import useCopy from "../../../hooks/useCopy";
import useFilters from "../../../hooks/useFilters";
import DashboardLayout from "../../../layouts/DashboardLayout";
import { toast } from "../../../lib/toast";
import type { ColumnsType } from "../../../types/global.types";

export default function Campaign() {
  const navigate = useNavigate();
  const handleCopy = useCopy();
  const [updatePromotion] = useUpdatePromotionMutation();
  // const [deletePromotion] = useDeletePromotionMutation();
  const {
    page,
    limit,
    handleLimit,
    handleSearch,
    handleFilter,
    handlePagination,
    filter,
    keyword,
  } = useFilters<{
    status?: string | undefined;
  }>();

  // Map UI status filter to API status values
  const statusFilter =
    filter?.status === "active"
      ? "ACTIVE"
      : filter?.status === "inactive"
        ? "INACTIVE"
        : undefined;

  const { data: promotionData, isLoading } = useGetPromotionsQuery({
    limit,
    q: keyword,
    status: statusFilter as any,
  });

  const handleStatusToggle = async (record: PromotionItem) => {
    const newStatus = record.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    const actionText = newStatus === "ACTIVE" ? "activate" : "inactive";

    Modal.confirm({
      title: `Are you sure you want to ${actionText} this campaign?`,
      content: `This will change the campaign status to ${newStatus.toLowerCase()}.`,
      okText: `Yes, ${actionText.charAt(0).toUpperCase() + actionText.slice(1)}`,
      okType: newStatus === "ACTIVE" ? "primary" : "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await updatePromotion({
            id: record.id,
            body: {
              name: record.name,
              code: record.code,
              type: record.type,
              status: newStatus,
              scope: record.scope,
              discountKind: record.discountKind,
              value: record.value,
              currency: record.currency,
              startsAt: record.startsAt,
              endsAt: record.endsAt,
              isHidden: record.isHidden,
              perUserLimit: record.perUserLimit,
              maxBudget: record.maxBudget,
              targets: {
                includeCourseUuids:
                  record.rules?.targets?.includeCourseUuids || [],
                organizationUuid: record.rules?.targets?.organizationUuid || "",
                productType: record.rules?.targets?.productType || "",
              },
              rules: record.rules || {},
            },
          }).unwrap();
          toast("success", `Campaign ${actionText}d successfully`);
        } catch (error: any) {
          toast(
            "error",
            error?.data?.message || `Failed to ${actionText} campaign`,
          );
        }
      },
    });
  };

  // const handleDelete = (id: string) => {
  //   Modal.confirm({
  //     title: "Are you sure you want to delete this campaign?",
  //     content: "This action cannot be undone.",
  //     okText: "Yes, Delete",
  //     okType: "danger",
  //     cancelText: "No",
  //     onOk: async () => {
  //       try {
  //         await deletePromotion(id).unwrap();
  //         toast("success", "Campaign deleted successfully");
  //       } catch (error: any) {
  //         toast("error", error?.data?.message || "Failed to delete campaign");
  //       }
  //     },
  //   });
  // };

  const statusOptions = [
    { label: "Active", value: "ACTIVE", className: "status-success" },
    { label: "Inactive", value: "INACTIVE", className: "status-error" },
    { label: "Archived", value: "ARCHIVED", className: "status-warning" },
  ];

  const columns: ColumnsType<PromotionItem> = [
    {
      key: "name",
      title: "Campaign Title",
      dataIndex: "name",
      render: (title: string) => (
        <span className="font-medium text-theme-slate-800">{title}</span>
      ),
    },
    {
      key: "offerType",
      title: "Offer Type",
      dataIndex: "type",
      render: (type: string) => (
        <span className="capitalize">{type?.toLowerCase()}</span>
      ),
    },
    {
      key: "coursesApplied",
      title: "Courses Applied",
      dataIndex: "rules",
      render: (rules: any) => {
        const count = rules?.targets?.includeCourseUuids?.length || 0;
        return count.toString().padStart(2, "0");
      },
      width: 150,
    },
    {
      key: "code",
      title: "Coupon Code",
      dataIndex: "code",
      render: (val: string, record) =>
        record?.type === "COUPON" ? (
          <Flex align="center" gap={4}>
            {val}{" "}
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined className="relative right-[1px]" />}
              onClick={() => handleCopy(val)}
            />
          </Flex>
        ) : (
          "-"
        ),
      width: 150,
    },
    {
      key: "offerSummary",
      title: "Offer Summary",
      render: (_: any, record: PromotionItem) => {
        let summary = "";
        if (record.discountKind === "PERCENT") {
          summary = `${record.value}% Off`;
        } else if (record.discountKind === "FIXED_AMOUNT") {
          summary = `৳${record.value} Off`;
        } else {
          summary = record.discountKind || "";
        }

        return (
          <span className="bg-[#FFF2E5] text-[#FF851B] px-2.5 py-1 rounded text-xs font-medium">
            {summary}
          </span>
        );
      },
    },
    {
      key: "validityPeriod",
      title: "Validity Period",
      render: (_: any, record: PromotionItem) => (
        <span className="text-nowrap">
          {record.startsAt ? dayjs(record.startsAt).format("DD-MM-YYYY") : "-"}{" "}
          to {record.endsAt ? dayjs(record.endsAt).format("DD-MM-YYYY") : "-"}
        </span>
      ),
    },
    {
      key: "budget",
      title: "Budget",
      render: (_: any, record: PromotionItem) => (
        <span className="text-nowrap">{`৳${
          record.budgetUsed?.toLocaleString() || 0
        } / ৳${record.maxBudget?.toLocaleString() || 0}`}</span>
      ),
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      render: (status: string) => (
        <StatusTag value={status} config={statusOptions} />
      ),
      width: 100,
    },
    {
      key: "action",
      title: "Actions",
      align: "end",
      width: 80,
      render: (_: any, record: PromotionItem) => (
        <Dropdown
          overlayClassName="w-36"
          placement="bottomRight"
          menu={{
            items: [
              {
                key: "view",
                label: (
                  <Flex align="center" gap={8}>
                    <Flex justify="center" className="w-6 text-theme-sky-500">
                      <ViewIcon size={24} />
                    </Flex>
                    <span className="text-sm font-medium">View</span>
                  </Flex>
                ),
                onClick: () => navigate(`view/${record?.id}`),
              },
              // {
              //   key: "edit",
              //   label: (
              //     <Flex align="center" gap={8}>
              //       <Flex
              //         justify="center"
              //         className="w-6 text-theme-yellow-500"
              //       >
              //         <EditIcon size={18} />
              //       </Flex>
              //       <span className="text-sm font-medium">Edit</span>
              //     </Flex>
              //   ),
              //   onClick: () => navigate(`update/${record?.id}`),
              // },
              {
                key: "status",
                label: (
                  <Flex align="center" gap={8}>
                    <Flex
                      justify="center"
                      className={`w-6 ${
                        record.status === "ACTIVE"
                          ? "text-theme-error-500"
                          : "text-theme-success-500"
                      }`}
                    >
                      {record.status === "ACTIVE" ? (
                        <DeleteIcon size={16} />
                      ) : (
                        <EditIcon size={18} />
                      )}
                    </Flex>
                    <span className="text-sm font-medium">
                      {record.status === "ACTIVE" ? "Inactive" : "Activate"}
                    </span>
                  </Flex>
                ),
                onClick: () => handleStatusToggle(record),
                // disabled: record.status === "ARCHIVED",
              },
              // {
              //   key: "delete",
              //   label: (
              //     <Flex align="center" gap={8}>
              //       <Flex justify="center" className="w-6 text-theme-error-500">
              //         <DeleteIcon size={16} />
              //       </Flex>
              //       <span className="text-sm font-medium">Delete</span>
              //     </Flex>
              //   ),
              //   onClick: () => handleDelete(record.id),
              // },
            ],
          }}
          trigger={["click"]}
        >
          <Button
            type="text"
            size="small"
            icon={<ActionIcon />}
            className="flex items-center justify-center text-theme-gray-400"
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <DashboardLayout title="Campaign">
      <CustomTitle
        title="Campaign"
        description="Create and manage promotional campaigns to increase enrollments and revenue"
      >
        <Link to={"create"}>
          <Button type="primary" className="btn-primary">
            <AddIcon /> Create Campaign
          </Button>
        </Link>
      </CustomTitle>

      <CustomTable
        total={undefined} // API doesn't provide total count
        page={page}
        limit={limit}
        loading={isLoading}
        columns={columns}
        dataSource={promotionData?.items || []}
        onLimitChange={handleLimit}
        onPaginate={(current, pageSize) => handlePagination(current, pageSize)}
        hideRowSelect={false}
        filterSection={
          <Flex align="center" gap={8}>
            <Input
              size="middle"
              placeholder="Search campaign"
              prefix={<SearchIcon size={14} />}
              className="max-w-[250px] min-w-[200px] input-bordered"
              onChange={handleSearch}
            />
            <Select
              allowClear
              size="middle"
              placeholder="Status"
              style={{ width: 120 }}
              className="input-bordered"
              options={[
                { label: "Active", value: "active" },
                { label: "Inactive", value: "inactive" },
              ]}
              onChange={(val) => handleFilter("status", val)}
            />
          </Flex>
        }
      />
    </DashboardLayout>
  );
}
